import React, { useRef, useState } from 'react';
import { SkyWrenchResult, SkyWrenchSection } from '../types';
import { resolveSkyWrenchReference, SkyWrenchReferenceAccess } from '../services/skyWrenchService';
import skyWrenchLogo from '../assets/skywrench-logo.png';
import {
  SkyWrenchReferenceCards,
  SkyWrenchReferenceViewer,
  uniqueSourceUris,
} from './SkyWrenchReferences';

/**
 * Props for the SkyWrenchModal component.
 */
interface SkyWrenchModalProps {
  result: SkyWrenchResult;
  isOpen: boolean;
  onClose: () => void;
  /**
   * When provided, shows a re-run control in the header that regenerates the
   * analysis for this defect. Omitted (e.g. read-only records) hides it.
   */
  onRegenerate?: () => void;
}

/**
 * Read-only popup that displays SkyWrench troubleshooting output for a defect.
 * Includes a close icon and a PDF download icon.
 */
const SkyWrenchModal: React.FC<SkyWrenchModalProps> = ({
  result,
  isOpen,
  onClose,
  onRegenerate,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [openingReferenceUri, setOpeningReferenceUri] = useState<string | null>(null);
  const [referenceError, setReferenceError] = useState<string>('');
  const [viewerDocument, setViewerDocument] = useState<SkyWrenchReferenceAccess | null>(null);

  if (!isOpen) return null;

  const handleOpenReference = async (sourceUri: string): Promise<void> => {
    setOpeningReferenceUri(sourceUri);
    setReferenceError('');
    try {
      const reference: SkyWrenchReferenceAccess = await resolveSkyWrenchReference(sourceUri);
      setViewerDocument(reference);
    } catch (error: unknown) {
      setReferenceError(
        error instanceof Error ? error.message : 'The selected reference could not be opened.',
      );
    } finally {
      setOpeningReferenceUri(null);
    }
  };

  /**
   * Generates a PDF from the SkyWrench result content and triggers a Save As dialog.
   * Uses the browser's print-to-PDF via a hidden iframe for maximum compatibility
   * without requiring additional dependencies.
   */
  const handleDownloadPdf = () => {
    const printContent = buildPrintHtml(result);
    const blob = new Blob([printContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    // Open a new window for the print dialog which allows Save as PDF
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.addEventListener('load', () => {
        printWindow.print();
        // Clean up blob URL after a delay to allow print dialog to use it
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      });
    } else {
      // Fallback: download as HTML file which user can print to PDF
      const link = document.createElement('a');
      link.href = url;
      link.download = `SkyWrench_${result.tailNumber}_Defect${result.defectId}_${new Date().toISOString().slice(0, 10)}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }
  };

  const resp = result.response;

  return (
    <div
      className="modal-overlay active"
      role="dialog"
      aria-modal="true"
      aria-label="SkyWrench Results"
    >
      <div className="modal skywrench-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header skywrench-modal-header">
          <h2>
            <img className="skywrench-icon-logo" src={skyWrenchLogo} alt="" aria-hidden="true" />
            <span className="skywrench-modal-title-text">
              SkyWrench Analysis — {result.defectDescription}
            </span>
          </h2>
          <div className="skywrench-header-actions">
            {onRegenerate && (
              <button
                className="skywrench-action-btn skywrench-regenerate-btn"
                type="button"
                onClick={onRegenerate}
                aria-label="Re-run SkyWrench analysis"
                title="Re-run SkyWrench analysis"
              >
                ✦
              </button>
            )}
            <button
              className="skywrench-action-btn"
              type="button"
              onClick={handleDownloadPdf}
              aria-label="Download as PDF"
              title="Download as PDF"
            >
              ⬇
            </button>
            <button className="modal-close" type="button" onClick={onClose} aria-label="Close">
              &times;
            </button>
          </div>
        </div>

        <div className="modal-body-scroll skywrench-modal-body" ref={contentRef}>
          {/* Query Context */}
          <div className="skywrench-section">
            <div className="skywrench-section-title">Query Details</div>
            <div className="skywrench-meta-grid">
              <div className="skywrench-meta-item">
                <span className="skywrench-meta-label">Aircraft</span>
                <span className="skywrench-meta-value">{result.aircraftType}</span>
              </div>
              <div className="skywrench-meta-item">
                <span className="skywrench-meta-label">Tail</span>
                <span className="skywrench-meta-value">{result.tailNumber}</span>
              </div>
              <div className="skywrench-meta-item">
                <span className="skywrench-meta-label">ATA Code</span>
                <span className="skywrench-meta-value">{result.ataCode}</span>
              </div>
              <div className="skywrench-meta-item">
                <span className="skywrench-meta-label">Carrier</span>
                <span className="skywrench-meta-value">{result.carrier}</span>
              </div>
              <div className="skywrench-meta-item">
                <span className="skywrench-meta-label">Station</span>
                <span className="skywrench-meta-value">{result.station}</span>
              </div>
              <div className="skywrench-meta-item">
                <span className="skywrench-meta-label">Queried</span>
                <span className="skywrench-meta-value">
                  {new Date(result.queriedAt).toLocaleString()}
                </span>
              </div>
            </div>
            <div className="skywrench-defect-desc">
              <span className="skywrench-meta-label">Defect Description</span>
              <span className="skywrench-meta-value">{result.defectDescription}</span>
            </div>
          </div>

          {/* Error State */}
          {result.status === 'error' && (
            <div className="skywrench-section skywrench-error">
              <div className="skywrench-section-title">Error</div>
              <p>{result.error || 'An unknown error occurred while querying SkyWrench.'}</p>
            </div>
          )}

          {/* Analysis sections returned by SkyWrench */}
          {resp?.sections && resp.sections.length > 0
            ? resp.sections.map((section: SkyWrenchSection) => {
                const referenceSection: boolean = isReferenceSection(section);
                return (
                  <div
                    key={section.section}
                    className={`skywrench-section${section.error ? ' skywrench-error' : ''}`}
                  >
                    <div className="skywrench-section-title">
                      {section.section}. {section.title}
                    </div>
                    {referenceSection ? (
                      <SkyWrenchReferenceCards
                        sourceUris={getSectionReferenceUris(section, resp)}
                        openingUri={openingReferenceUri}
                        error={referenceError}
                        onOpen={handleOpenReference}
                      />
                    ) : (
                      <p className="skywrench-text">{section.content}</p>
                    )}
                  </div>
                );
              })
            : result.status === 'complete' && (
                <div className="skywrench-section">
                  <p className="skywrench-text">No analysis sections were returned.</p>
                </div>
              )}
        </div>
      </div>
      <SkyWrenchReferenceViewer document={viewerDocument} onClose={() => setViewerDocument(null)} />
    </div>
  );
};

/** True for SkyWrench's dedicated references section. */
function isReferenceSection(section: SkyWrenchSection): boolean {
  return section.section === '9' || section.title.trim().toUpperCase() === 'REFERENCES';
}

/** Collects string source URIs from an otherwise forward-compatible response object. */
function getSourceUris(value: unknown): string[] {
  if (!value || typeof value !== 'object' || !('sourceUris' in value)) return [];
  const sourceUris: unknown = value.sourceUris;
  if (!Array.isArray(sourceUris)) return [];
  return sourceUris.filter((uri: unknown): uri is string => typeof uri === 'string');
}

/** Uses complete result sources for Section 9, with section sources as fallback. */
function getSectionReferenceUris(section: SkyWrenchSection, response: unknown): string[] {
  if (!isReferenceSection(section)) return [];
  return uniqueSourceUris([...getSourceUris(response), ...getSourceUris(section)]);
}
/**
 * Builds a standalone HTML document for print/PDF generation.
 */
function buildPrintHtml(result: SkyWrenchResult): string {
  const resp = result.response;
  let sections = '';

  sections += `<h2>SkyWrench Analysis — Defect ${result.defectId}</h2>`;
  sections += `<table class="meta"><tr><td><b>Aircraft:</b> ${escapeHtml(result.aircraftType)}</td><td><b>Tail:</b> ${escapeHtml(result.tailNumber)}</td><td><b>ATA Code:</b> ${escapeHtml(result.ataCode)}</td></tr>`;
  sections += `<tr><td><b>Carrier:</b> ${escapeHtml(result.carrier)}</td><td><b>Station:</b> ${escapeHtml(result.station)}</td><td><b>Queried:</b> ${new Date(result.queriedAt).toLocaleString()}</td></tr></table>`;
  sections += `<p><b>Defect Description:</b> ${escapeHtml(result.defectDescription)}</p>`;

  if (result.status === 'error') {
    sections += `<h3>Error</h3><p>${escapeHtml(result.error || 'Unknown error')}</p>`;
  }

  if (resp?.sections?.length) {
    resp.sections.forEach((section: SkyWrenchSection) => {
      sections += `<h3>${escapeHtml(section.section)}. ${escapeHtml(section.title)}</h3>`;
      if (!isReferenceSection(section)) {
        sections += `<p>${escapeHtml(section.content)}</p>`;
      }
    });
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>SkyWrench Report - ${escapeHtml(result.tailNumber)} Defect ${result.defectId}</title>
<style>body{font-family:Arial,sans-serif;margin:40px;color:#1a1a1a}h2{color:#1e3a5f;border-bottom:2px solid #1e3a5f;padding-bottom:8px}h3{color:#2c5282;margin-top:24px}table.meta{width:100%;border-collapse:collapse;margin:12px 0}table.meta td{padding:4px 12px 4px 0}ol,ul{padding-left:24px}li{margin-bottom:6px}p{line-height:1.6;white-space:pre-wrap}@media print{body{margin:20px}}</style>
</head><body>${sections}</body></html>`;
}

/**
 * Escapes HTML-significant characters so free-text SkyWrench content cannot
 * inject markup into the generated print document.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default SkyWrenchModal;
